<template>
  <AdminLayout>
    <div class="p-6 lg:p-8">
      <!-- 페이지 헤더 -->
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-blue-600/10 rounded-xl flex items-center justify-center">
            <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/>
            </svg>
          </div>
          <div>
            <h1 class="text-2xl font-bold text-slate-800">포인트 충전 관리</h1>
            <p class="text-sm text-slate-500">파트너스 포인트 충전 신청 승인/거절</p>
          </div>
        </div>
        <button
          @click="loadChargeRequests"
          class="px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition"
        >
          새로고침
        </button>
      </div>

      <!-- 통계 카드 -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div class="bg-white rounded-xl border border-slate-200 p-4">
          <p class="text-sm text-slate-500 mb-1">전체 신청</p>
          <p class="text-2xl font-bold text-slate-800">{{ stats.total }}</p>
        </div>
        <div class="bg-amber-50 rounded-xl border border-amber-200 p-4">
          <p class="text-sm text-amber-600 mb-1">대기 중</p>
          <p class="text-2xl font-bold text-amber-700">{{ stats.pending }}</p>
        </div>
        <div class="bg-green-50 rounded-xl border border-green-200 p-4">
          <p class="text-sm text-green-600 mb-1">승인 완료</p>
          <p class="text-2xl font-bold text-green-700">{{ stats.approved }}</p>
        </div>
        <div class="bg-red-50 rounded-xl border border-red-200 p-4">
          <p class="text-sm text-red-600 mb-1">거절</p>
          <p class="text-2xl font-bold text-red-700">{{ stats.rejected }}</p>
        </div>
      </div>

      <!-- 필터 -->
      <div class="bg-white rounded-xl border border-slate-200 p-4 mb-6">
        <div class="flex flex-wrap gap-2">
          <button
            v-for="filter in filters"
            :key="filter.value"
            @click="currentFilter = filter.value; loadChargeRequests()"
            :class="[
              'px-4 py-2 rounded-lg text-sm font-medium transition',
              currentFilter === filter.value
                ? 'bg-blue-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            ]"
          >
            {{ filter.label }}
          </button>
        </div>
      </div>

      <!-- 신청 목록 -->
      <div class="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div v-if="loading" class="flex justify-center py-12">
          <div class="w-8 h-8 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin"></div>
        </div>

        <div v-else-if="chargeRequests.length === 0" class="text-center py-12 text-slate-400">
          신청 내역이 없습니다
        </div>

        <table v-else class="w-full">
          <thead class="bg-slate-50 border-b border-slate-200">
            <tr>
              <th class="px-4 py-3 text-left text-sm font-medium text-slate-600">신청자</th>
              <th class="px-4 py-3 text-left text-sm font-medium text-slate-600">입금자명</th>
              <th class="px-4 py-3 text-right text-sm font-medium text-slate-600">금액</th>
              <th class="px-4 py-3 text-right text-sm font-medium text-slate-600">적립 포인트</th>
              <th class="px-4 py-3 text-center text-sm font-medium text-slate-600">상태</th>
              <th class="px-4 py-3 text-left text-sm font-medium text-slate-600">신청일</th>
              <th class="px-4 py-3 text-center text-sm font-medium text-slate-600">액션</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100">
            <tr
              v-for="req in chargeRequests"
              :key="req.id"
              class="hover:bg-slate-50"
            >
              <td class="px-4 py-4">
                <p class="font-medium text-slate-800">{{ req.partnerName || '-' }}</p>
                <p class="text-sm text-slate-400">{{ req.partnerEmail }}</p>
              </td>
              <td class="px-4 py-4 text-slate-700">
                {{ req.depositorName }}
              </td>
              <td class="px-4 py-4 text-right font-medium text-slate-800">
                {{ req.packagePrice?.toLocaleString() }}원
              </td>
              <td class="px-4 py-4 text-right">
                <span class="font-medium text-blue-600">{{ req.totalAmount?.toLocaleString() }}P</span>
                <span v-if="req.packageBonus" class="text-xs text-green-600 ml-1">(+{{ req.packageBonus?.toLocaleString() }})</span>
              </td>
              <td class="px-4 py-4 text-center">
                <span :class="[
                  'px-3 py-1 text-xs font-medium rounded-full',
                  req.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                  req.status === 'approved' ? 'bg-green-100 text-green-700' :
                  req.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'
                ]">
                  {{ getStatusLabel(req.status) }}
                </span>
              </td>
              <td class="px-4 py-4 text-sm text-slate-500">
                {{ formatDate(req.createdAt) }}
              </td>
              <td class="px-4 py-4 text-center">
                <div v-if="req.status === 'pending'" class="flex items-center justify-center gap-2">
                  <button
                    @click="handleApprove(req)"
                    :disabled="processing === req.id"
                    class="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                  >
                    승인
                  </button>
                  <button
                    @click="handleReject(req)"
                    :disabled="processing === req.id"
                    class="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition disabled:opacity-50"
                  >
                    거절
                  </button>
                </div>
                <span v-else class="text-sm text-slate-400">-</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 거절 모달 -->
      <div v-if="showRejectModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50" @click.self="showRejectModal = false">
        <div class="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
          <h3 class="text-xl font-bold text-slate-800 mb-4">충전 신청 거절</h3>

          <div v-if="rejectTarget" class="bg-slate-50 rounded-xl p-4 mb-4">
            <p class="text-sm text-slate-600">
              <strong>{{ rejectTarget.partnerName }}</strong>님의
              <strong>{{ rejectTarget.packagePrice?.toLocaleString() }}원</strong> 충전 신청을 거절합니다.
            </p>
          </div>

          <div class="mb-4">
            <label class="block text-sm font-medium text-slate-700 mb-2">거절 사유 (선택)</label>
            <textarea
              v-model="rejectReason"
              rows="3"
              placeholder="거절 사유를 입력하세요"
              class="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 transition resize-none"
            ></textarea>
          </div>

          <div class="flex gap-3">
            <button
              @click="showRejectModal = false"
              class="flex-1 py-3 border border-slate-200 rounded-xl font-medium text-slate-600 hover:bg-slate-50 transition"
            >
              취소
            </button>
            <button
              @click="confirmReject"
              :disabled="processing"
              class="flex-1 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition disabled:opacity-50"
            >
              {{ processing ? '처리 중...' : '거절하기' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import AdminLayout from '@/components/admin/common/AdminLayout.vue'
import { usePointChargeAdmin } from '@/composables/admin/usePointChargeAdmin'

export default {
  name: 'PointChargeManageView',
  components: {
    AdminLayout
  },
  setup() {
    const {
      chargeRequests,
      loading,
      loadAllChargeRequests,
      approveChargeRequest,
      rejectChargeRequest
    } = usePointChargeAdmin()

    const currentFilter = ref('pending')
    const processing = ref(null)
    const showRejectModal = ref(false)
    const rejectTarget = ref(null)
    const rejectReason = ref('')

    const filters = [
      { label: '대기 중', value: 'pending' },
      { label: '승인 완료', value: 'approved' },
      { label: '거절', value: 'rejected' },
      { label: '전체', value: 'all' }
    ]

    const stats = computed(() => {
      const all = chargeRequests.value
      return {
        total: all.length,
        pending: all.filter(r => r.status === 'pending').length,
        approved: all.filter(r => r.status === 'approved').length,
        rejected: all.filter(r => r.status === 'rejected').length
      }
    })

    const loadChargeRequests = async () => {
      await loadAllChargeRequests(currentFilter.value)
    }

    const handleApprove = async (req) => {
      if (!confirm(`${req.partnerName}님에게 ${req.totalAmount?.toLocaleString()}P를 적립하시겠습니까?`)) return

      processing.value = req.id
      const result = await approveChargeRequest(req.id)
      processing.value = null

      if (result.success) {
        alert('승인 완료! 포인트가 적립되었습니다.')
        await loadChargeRequests()
      } else {
        alert('승인 실패: ' + result.error)
      }
    }

    const handleReject = (req) => {
      rejectTarget.value = req
      rejectReason.value = ''
      showRejectModal.value = true
    }

    const confirmReject = async () => {
      if (!rejectTarget.value) return

      processing.value = rejectTarget.value.id
      const result = await rejectChargeRequest(rejectTarget.value.id, rejectReason.value)
      processing.value = null

      if (result.success) {
        showRejectModal.value = false
        rejectTarget.value = null
        alert('거절 처리되었습니다.')
        await loadChargeRequests()
      } else {
        alert('거절 실패: ' + result.error)
      }
    }

    const getStatusLabel = (status) => {
      switch (status) {
        case 'pending': return '대기 중'
        case 'approved': return '승인 완료'
        case 'rejected': return '거절됨'
        case 'cancelled': return '취소됨'
        default: return status
      }
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleDateString('ko-KR', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    onMounted(async () => {
      // 전체 데이터 로드 후 통계용
      await loadAllChargeRequests('all')
      // 필터 적용
      await loadChargeRequests()
    })

    return {
      chargeRequests,
      loading,
      currentFilter,
      filters,
      stats,
      processing,
      showRejectModal,
      rejectTarget,
      rejectReason,
      loadChargeRequests,
      handleApprove,
      handleReject,
      confirmReject,
      getStatusLabel,
      formatDate
    }
  }
}
</script>
